import { App as AntApp, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconCheck, IconCopy, IconDownload, IconLink, IconSearch } from "../components/Icons";
import { Button, Empty, PageHeader, Pill, Skeleton, Stat } from "../components/ui";
import { safeGet } from "../lib/api";
import { duration, fmtDateDot, fmtTime, int, isNum, parseDate } from "../lib/format";

const LEVELS = ["ALL", "INFO", "WARN", "ERROR"];
const RANGES = [
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 },
  { label: "All runs", value: 0 },
];

function normLevel(l) {
  const u = String(l || "INFO").toUpperCase();
  if (u.startsWith("WARN")) return "WARN";
  if (u === "ERROR" || u === "CRITICAL" || u === "FATAL") return "ERROR";
  if (u === "DEBUG") return "DEBUG";
  return "INFO";
}

function statusPill(run) {
  const s = run.status;
  if (s === "success") return <Pill kind="good">Success</Pill>;
  if (s === "failed") return <Pill kind="bad">Failed</Pill>;
  if (s === "running") return <Pill kind="info">Running</Pill>;
  if (s === "stale") return <Pill kind="warn">Stale</Pill>;
  return <Pill kind="neutral">{s || "—"}</Pill>;
}

function triggerNote(run) {
  const parts = [];
  const t = { manual: "Manual run", schedule: "Scheduled", api: "API run", ci: "CI run" }[run.trigger] || run.trigger || "Run";
  parts.push(run.market && run.market !== "ALL" ? `${t} · ${run.market}` : t);
  if (run.rebalance && (run.rebalance.buys || run.rebalance.sells)) parts.push(`rebalance ${run.rebalance.buys} buys / ${run.rebalance.sells} sells`);
  else if (run.rebalance) parts.push("no rebalance");
  if (isNum(run.telegram_sent)) parts.push(`Telegram ${run.telegram_sent} message${run.telegram_sent === 1 ? "" : "s"}`);
  if (run.errors) parts.push(`${run.errors} error${run.errors === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

export default function Logs() {
  const { message } = AntApp.useApp();
  const [runs, setRuns] = useState(null);
  const [selected, setSelected] = useState(null);
  const [range, setRange] = useState(14);
  const [level, setLevel] = useState("ALL");
  const [q, setQ] = useState("");
  const [follow, setFollow] = useState(true);
  const [log, setLog] = useState({ lines: [], total: 0, loading: true });
  const bodyRef = useRef(null);

  useEffect(() => {
    (async () => {
      const list = await safeGet("/api/runs", []);
      const arr = Array.isArray(list) ? list : [];
      setRuns(arr);
      setSelected((s) => s || arr[0]?.id || null);
    })();
  }, []);

  const run = useMemo(() => (runs || []).find((r) => r.id === selected) || null, [runs, selected]);

  const loadLog = useCallback(async () => {
    const params = new URLSearchParams();
    if (selected) params.set("run", selected);
    params.set("level", level);
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "2000");
    const data = await safeGet(`/api/logs?${params.toString()}`, null);
    setLog({ lines: Array.isArray(data?.lines) ? data.lines : [], total: data?.total || 0, loading: false });
  }, [selected, level, q]);

  useEffect(() => {
    if (runs === null) return;
    setLog((l) => ({ ...l, loading: true }));
    const t = setTimeout(loadLog, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadLog, runs, q]);

  const running = run?.status === "running";
  useEffect(() => {
    if (!follow || !running) return undefined;
    const t = setInterval(async () => {
      await loadLog();
      const list = await safeGet("/api/runs", null);
      if (Array.isArray(list)) setRuns(list);
    }, 10000);
    return () => clearInterval(t);
  }, [follow, running, loadLog]);

  useEffect(() => {
    if (follow && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [log.lines, follow]);

  const visibleRuns = useMemo(() => {
    if (!runs) return [];
    if (!range) return runs;
    const cutoff = Date.now() - range * 86400000;
    return runs.filter((r) => {
      const d = parseDate(r.started_at || r.id);
      return !d || d.getTime() >= cutoff;
    });
  }, [runs, range]);

  const copyLog = async () => {
    const text = log.lines.map((l) => `${fmtTime(l.ts)}  ${normLevel(l.level).padEnd(5)}  ${l.msg}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      message.success(`Copied ${log.lines.length} lines`);
    } catch (e) {
      message.error("Clipboard unavailable");
    }
  };

  const tg = run ? `${int(run.telegram_sent)} delivered` : "—";

  return (
    <>
      <PageHeader title="Logs" sub="Scan runs, rebalance plans and Telegram delivery — from screener.log and GitHub Actions">
        <Tooltip title="Coming soon">
          <span>
            <Button icon={<IconDownload />} disabled aria-disabled="true">
              Download signals_log.csv
            </Button>
          </span>
        </Tooltip>
        {run?.actions_url && (
          <a className="btn" href={run.actions_url} target="_blank" rel="noreferrer">
            <IconLink /> Open in GitHub Actions
          </a>
        )}
      </PageHeader>

      <div className="logs-grid" style={{ display: "grid", gridTemplateColumns: "360px minmax(0, 1fr)", gap: 20, flexGrow: 1, minHeight: 0 }}>
        <section aria-label="Scan runs" className="card" style={{ display: "flex", flexDirection: "column", overflow: "hidden", alignSelf: "start" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Runs</span>
            <select
              aria-label="Filter runs"
              className="select"
              style={{ minHeight: 32, padding: "6px 8px", fontSize: 12, borderRadius: 6 }}
              value={range}
              onChange={(e) => setRange(Number(e.target.value))}
            >
              {RANGES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", overflow: "auto", maxHeight: "70vh" }}>
            {runs === null && (
              <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                <Skeleton height={14} width="60%" />
                <Skeleton height={12} />
                <Skeleton height={12} width="80%" />
              </div>
            )}
            {runs !== null && visibleRuns.length === 0 && (
              <Empty title="No runs">Runs appear here after the first scan.</Empty>
            )}
            {visibleRuns.map((r) => {
              const active = r.id === selected;
              const failed = r.status === "failed";
              return (
                <button
                  key={r.id}
                  type="button"
                  aria-current={active ? "true" : undefined}
                  onClick={() => setSelected(r.id)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    padding: "14px 16px",
                    borderLeft: `3px solid ${active ? "var(--accent)" : "transparent"}`,
                    background: active ? "var(--accentSoft)" : "transparent",
                    border: 0,
                    borderBottom: "1px solid var(--border)",
                    textAlign: "left",
                    cursor: "pointer",
                    color: "inherit",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{fmtDateDot(r.started_at || r.id)}</span>
                    {statusPill(r)}
                  </div>
                  <div className="num" style={{ fontSize: 12, color: "var(--muted)" }}>
                    {failed && !r.hits
                      ? `${int(r.scored)} scored · aborted`
                      : `${int(r.scored)} scored · ${int(r.hits)} hits · ${int(r.buys)} BUY · ${duration(r.duration_s)}`}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{triggerNote(r)}</div>
                </button>
              );
            })}
          </div>
        </section>

        <section aria-label="Log output" style={{ display: "flex", flexDirection: "column", gap: 14, minHeight: 0, minWidth: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12 }}>
            <Stat compact label="Duration" value={run ? duration(run.duration_s) : "—"} />
            <Stat compact label="Cache hits" value={run ? int(run.cache_hits) : "—"} suffix={run ? `/ ${int(run.scored)}` : undefined} />
            <Stat compact label="Warnings" value={run ? int(run.warnings) : "—"} color={run?.warnings ? "var(--warn)" : undefined} />
            <div className="stat compact">
              <span className="label">Telegram</span>
              <span
                style={{ fontSize: 14, fontWeight: 500, color: run?.telegram_sent ? "var(--good)" : "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}
              >
                {run?.telegram_sent ? <IconCheck /> : null}
                {tg}
              </span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label className="search-box" style={{ flexGrow: 1, width: "auto" }}>
              <IconSearch />
              <span className="sr-only">Search log lines</span>
              <input
                type="search"
                placeholder="Filter lines… e.g. HCLTECH, FXIgnored, 429"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            {LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                className={`chip dark ${l === "WARN" ? "warn-text" : l === "ERROR" ? "bad-text" : ""}`}
                aria-pressed={level === l}
                onClick={() => setLevel(l)}
              >
                {l === "ALL" ? "All" : l}
              </button>
            ))}
            <label className="check" style={{ fontSize: 12, color: "var(--muted)", marginLeft: 6 }}>
              <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow
            </label>
          </div>

          <div className="terminal">
            <div className="terminal-head">
              <span className="num">
                logs/screener.log{run ? ` · run ${fmtDateDot(run.started_at || run.id)}` : ""} · {int(log.total || log.lines.length)} lines
              </span>
              <button type="button" className="btn ghost" style={{ color: "var(--onSidebarMuted)", fontSize: 12 }} onClick={copyLog}>
                <IconCopy /> Copy
              </button>
            </div>
            <div className="terminal-body" ref={bodyRef}>
              {log.loading && log.lines.length === 0 && (
                <div className="ln"><span className="ts">…</span><span className="lv INFO">INFO</span><span className="msg">Loading…</span></div>
              )}
              {!log.loading && log.lines.length === 0 && (
                <div className="ln"><span className="ts">--:--:--</span><span className="lv INFO">INFO</span><span className="msg" style={{ color: "#7C7A70" }}>No log lines{run ? " for this run" : ""}.</span></div>
              )}
              {log.lines.map((l, i) => {
                const lv = normLevel(l.level);
                return (
                  <div className="ln" key={i}>
                    <span className="ts">{fmtTime(l.ts)}</span>
                    <span className={`lv ${lv}`}>{lv}</span>
                    <span className="msg">{l.logger && !String(l.msg).startsWith("[") ? `[${l.logger}] ` : ""}{l.msg}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
